import { useEffect, useRef } from "react";
import gsap from "gsap";
import { useMotion } from "./MotionProvider";
import { useFloatingAnimation } from "./hooks";

// ---------------------------------------------------------------------------
// <BackgroundStack> — the site's living backdrop. Five layers, five clocks:
//   1. Gradient  — gold field, 24s transform loop (spec: 20–30s)
//   2. Blur wash — large soft-blurred pool, organic Floating drift
//   3. Glow      — breathing opacity 0.35 → 0.60 → 0.35 over 8s
//   4. Grid      — faint gold lines, radially masked, 60s drift
//   5. Noise     — the existing static `.grain` overlay (site-wide, above all)
// Fixed, pointer-transparent, transform/opacity only. Everything is tuned a
// stop below perception threshold — felt, not watched. Disabled entirely
// under prerender / reduced-motion (layers render at rest).
// ---------------------------------------------------------------------------

export default function BackgroundStack() {
    const { enabled } = useMotion();
    const gradientRef = useRef(null);
    const glowRef = useRef(null);
    const gridRef = useRef(null);
    const blurRef = useFloatingAnimation({ amplitude: [10, 24], rotate: false });

    // Gradient field: slow independent wander, 24s
    useEffect(() => {
        if (!enabled || !gradientRef.current) return;
        const ctx = gsap.context(() => {
            gsap.to(gradientRef.current, {
                xPercent: 3, yPercent: -2, rotate: 1.2, scale: 1.05,
                duration: 24, repeat: -1, yoyo: true, ease: "sine.inOut",
            });
        }, gradientRef);
        return () => ctx.revert();
    }, [enabled]);

    // Glow breathing: 0.35 → 0.60 → 0.35, full cycle 8s
    useEffect(() => {
        if (!enabled || !glowRef.current) return;
        const ctx = gsap.context(() => {
            gsap.fromTo(glowRef.current, { opacity: 0.35 }, {
                opacity: 0.6, duration: 4, repeat: -1, yoyo: true, ease: "sine.inOut",
            });
        }, glowRef);
        return () => ctx.revert();
    }, [enabled]);

    // Grid: barely-there drift on a long clock
    useEffect(() => {
        if (!enabled || !gridRef.current) return;
        const ctx = gsap.context(() => {
            gsap.to(gridRef.current, {
                x: 36, y: -24, duration: 60, repeat: -1, yoyo: true, ease: "sine.inOut",
            });
        }, gridRef);
        return () => ctx.revert();
    }, [enabled]);

    return (
        <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
            {/* 1 — Gradient field */}
            <div ref={gradientRef} className="absolute -inset-[10%] will-change-transform">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_15%,rgba(198,167,107,0.05),transparent_55%),radial-gradient(ellipse_at_20%_85%,rgba(198,167,107,0.04),transparent_50%)]" />
            </div>

            {/* 2 — Blur wash */}
            <div
                ref={blurRef}
                className="absolute left-[55%] top-[30%] h-[60vmin] w-[60vmin] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(198,167,107,0.045),transparent_70%)] blur-3xl will-change-transform"
            />

            {/* 3 — Breathing glow */}
            <div
                ref={glowRef}
                style={{ opacity: 0.35 }}
                className="absolute left-1/2 top-[60%] h-[50vmin] w-[80vmin] -translate-x-1/2 bg-[radial-gradient(ellipse,rgba(198,167,107,0.08),transparent_65%)] will-change-[opacity]"
            />

            {/* 4 — Grid, radially masked so it dissolves toward the edges */}
            <div
                ref={gridRef}
                className="absolute -inset-[8%] will-change-transform opacity-[0.5]"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(198,167,107,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(198,167,107,0.05) 1px, transparent 1px)",
                    backgroundSize: "88px 88px",
                    maskImage: "radial-gradient(ellipse at 50% 40%, black 20%, transparent 72%)",
                    WebkitMaskImage: "radial-gradient(ellipse at 50% 40%, black 20%, transparent 72%)",
                }}
            />
            {/* 5 — Noise: static site-wide .grain overlay (defined in index.css) */}
        </div>
    );
}
